/******************************************************************************
 * Pure helper: summarize photo category tags into per-category counts, for the
 * coverage line shown under each BEFORE/AFTER photo section. Tagged categories are
 * listed in category-order (or first-appearance order if not set), the untagged
 * bucket (label null) last.
 *****************************************************************************/

export const UNTAGGED_KEY = "__untagged__";

/** Minimal shape read by these helpers — any photo/media item carrying a tag. */
export interface PhotoTag {
  categoryId?: string | null;
  categoryLabel?: string | null;
  categoryOrder?: number | null;
}

/**
 * photoCategoryKey: the grouping/filter key for a photo — its categoryId (falling
 * back to the label) when tagged, or UNTAGGED_KEY otherwise. Shared by the summary
 * and the tag filter so their keys always match.
 * @param photo - the photo/media item
 */
export function photoCategoryKey(photo: PhotoTag): string {
  const label = photo.categoryLabel || null;
  return label ? (photo.categoryId || label) : UNTAGGED_KEY;
}

export interface PhotoCategoryCount {
  key: string;            // categoryId (or label) for tagged, UNTAGGED_KEY otherwise
  label: string | null;   // category label; null for the untagged bucket
  count: number;
  order?: number | null;
}

/**
 * summarizePhotoCategories: count photos per category tag.
 * @param photos - the photos of a single section (e.g. one phase)
 * @return - ordered category counts (untagged last), empty when no photos
 */
export function summarizePhotoCategories(photos: PhotoTag[]): PhotoCategoryCount[] {
  const byKey: Record<string, PhotoCategoryCount> = {};
  const firstAppearance: Record<string, number> = {};
  let appearanceIndex = 0;

  (photos || []).forEach((p) => {
    const key = photoCategoryKey(p);
    if (!byKey[key]) {
      byKey[key] = {
        key,
        label: p.categoryLabel || null,
        count: 0,
        order: p.categoryOrder !== undefined ? p.categoryOrder : null
      };
      firstAppearance[key] = appearanceIndex++;
    }
    byKey[key].count += 1;
  });

  return Object.values(byKey).sort((a, b) => {
    // Untagged bucket always goes last
    if (a.key === UNTAGGED_KEY) return 1;
    if (b.key === UNTAGGED_KEY) return -1;

    // If both have category order, sort by order
    const hasOrderA = a.order !== null && a.order !== undefined;
    const hasOrderB = b.order !== null && b.order !== undefined;
    if (hasOrderA && hasOrderB) {
      if (a.order !== b.order) {
        return (a.order as number) - (b.order as number);
      }
    } else if (hasOrderA) {
      return -1;
    } else if (hasOrderB) {
      return 1;
    }

    return firstAppearance[a.key] - firstAppearance[b.key];
  });
}

/**
 * hasTaggedPhotos: whether any photo carries a category tag.
 * @param photos - the photos to inspect
 */
export function hasTaggedPhotos(photos: PhotoTag[]): boolean {
  return (photos || []).some((p) => !!p.categoryLabel);
}
